import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { emailService } from "@/services/emailService";
import { reportService } from "@/services/reportService";
import type { WeeklyReport } from "@/types/WeeklyReport";
import { format, isBefore, parse, parseISO, isValid } from "date-fns";
import { CalendarIcon, Edit, Eye, Mail, Send, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

// Helper function to format dateRange (handles both string and object formats)
const formatDateRange = (
  dateRange: string | { from?: string; to?: string } | undefined,
  fallbackDate?: string,
): string => {
  if (!dateRange) {
    if (fallbackDate) {
      try {
        const date = parseISO(fallbackDate);
        if (isValid(date)) {
          return format(date, "MMM d, yyyy");
        }
      } catch (error) {
        console.warn("Error parsing fallback date:", fallbackDate, error);
      }
    }
    return "No date";
  }

  // If it's a string, return it as-is
  if (typeof dateRange === "string") {
    return dateRange;
  }

  // If it's an object with from/to, format it
  if (typeof dateRange === "object" && dateRange !== null) {
    const { from, to } = dateRange;
    if (from && to) {
      try {
        const fromDate = parseISO(from);
        const toDate = parseISO(to);
        if (isValid(fromDate) && isValid(toDate)) {
          return `${format(fromDate, "MMM d, yyyy")} - ${format(toDate, "MMM d, yyyy")}`;
        }
      } catch (error) {
        console.warn("Error parsing dateRange object:", dateRange, error);
      }
    }
    if (from) {
      try {
        const fromDate = parseISO(from);
        if (isValid(fromDate)) {
          return format(fromDate, "MMM d, yyyy");
        }
      } catch (error) {
        console.warn("Error parsing from date:", from, error);
      }
    }
  }

  // Fallback to fallbackDate if available
  if (fallbackDate) {
    try {
      const date = parseISO(fallbackDate);
      if (isValid(date)) {
        return format(date, "MMM d, yyyy");
      }
    } catch (error) {
      console.warn("Error parsing fallback date:", fallbackDate, error);
    }
  }

  return "No date";
};

// Custom toast functions with colors
const showSuccessToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#dcfce7",
      "--toast-color": "#166534",
      "--toast-border": "1px solid #bbf7d0",
    } as React.CSSProperties,
  });
};

const showInfoToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#ffffff",
      "--toast-color": "#374151",
      "--toast-border": "1px solid #e5e7eb",
    } as React.CSSProperties,
  });
};

const showErrorToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#fef2f2",
      "--toast-color": "#dc2626",
      "--toast-border": "1px solid #fecaca",
    } as React.CSSProperties,
  });
};

interface EmailListProps {
  reports: WeeklyReport[];
  loading?: boolean;
  onEditReport?: (report: WeeklyReport) => void;
}

type StatusFilter = "all" | "sent";

const EmailList: React.FC<EmailListProps> = ({
  reports,
  loading = false,
  onEditReport,
}) => {
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | null>(
    null,
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({
    title: "",
    description: "",
    confirmLabel: "Continue",
  });
  const [reportToSend, setReportToSend] = useState<WeeklyReport | null>(null);

  // Filter reports based on current filter state
  const filteredReports = useMemo(() => {
    console.log(
      `Filtering ${reports.length} reports with status: ${statusFilter}`,
      reports,
    );

    // Early return if no reports
    if (!reports || reports.length === 0) {
      console.log("No reports to filter");
      return [];
    }

    let result = [...reports];

    // Filter by status
    if (statusFilter === "sent") {
      result = result.filter((report) => report.sentByEmail === true);
      console.log(`Found ${result.length} sent reports`);
    }

    // Always apply date filters if set
    if (startDate || endDate) {
      // Convert date strings to Date objects
      const from = startDate ? new Date(startDate) : new Date(0);
      const to = endDate ? new Date(endDate) : new Date(Date.now() + 86400000);
      to.setHours(23, 59, 59, 999); // Include the entire "to" day

      result = result.filter((report) => {
        try {
          // Parse report date using various formats
          let reportDate: Date | null = null;

          // Check if the report has a date field
          if (report.date) {
            reportDate = parseISO(report.date);
          }
          // If no reportDate yet but there's a dateRange that looks like an ISO date
          else if (
            report.dateRange &&
            /\d{4}-\d{2}-\d{2}/.test(report.dateRange)
          ) {
            // Try to extract ISO dates from dateRange
            const isoMatch = report.dateRange.match(/(\d{4}-\d{2}-\d{2})/);
            if (isoMatch && isoMatch[1]) {
              reportDate = parseISO(isoMatch[1]);
            }
          }
          // Try to extract from dateRange like "Mar 24, 2023 - Mar 30, 2023"
          else if (report.dateRange) {
            const dateMatch = report.dateRange.match(
              /([A-Za-z]+\s\d{1,2},\s\d{4})/,
            );
            if (dateMatch && dateMatch[1]) {
              reportDate = parse(dateMatch[1], "MMM d, yyyy", new Date());
            }
          }

          // If still no valid date, use current date and log a warning
          if (!reportDate) {
            console.warn(
              `No valid date found for report ${report.id}, using current date.`,
            );
            reportDate = new Date();
          }

          // Check if the date falls within the range
          return reportDate >= from && reportDate <= to;
        } catch (error) {
          console.error("Error filtering report by date:", error);
          return false;
        }
      });
    }

    return result;
  }, [reports, statusFilter, startDate, endDate]);

  const clearFilters = () => {
    setStatusFilter("all");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const clearDateFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  // Show confirmation before sending
  const confirmSendEmail = (report: WeeklyReport) => {
    // Check if there are recipients
    const hasRecipients =
      (Array.isArray(report.to) && report.to.length > 0) ||
      (Array.isArray(report.cc) && report.cc.length > 0) ||
      (Array.isArray(report.bcc) && report.bcc.length > 0);

    if (!hasRecipients) {
      setDialogMessage({
        title: "No Recipients",
        description:
          "Please add at least one recipient email address before sending.",
        confirmLabel: "Edit Report",
      });
      setShowConfirmDialog(true);
      return;
    }

    // Calculate total number of recipients
    const toCount = Array.isArray(report.to) ? report.to.length : 0;
    const ccCount = Array.isArray(report.cc) ? report.cc.length : 0;
    const bccCount = Array.isArray(report.bcc) ? report.bcc.length : 0;
    const totalRecipients = toCount + ccCount + bccCount;

    // Save report reference for sending
    setReportToSend(report);

    // Show confirmation dialog with correct count
    setDialogMessage({
      title: "Confirm Send Email",
      description: `Are you sure you want to send this email to ${totalRecipients} recipient${totalRecipients !== 1 ? "s" : ""}?`,
      confirmLabel: "Verify & Send",
    });
    setShowConfirmDialog(true);
  };

  // Verify token and send email
  const verifyAndSendEmail = async () => {
    if (!reportToSend) return;

    try {
      setIsLoading(true);

      // Close the confirmation dialog
      setShowConfirmDialog(false);

      // Directly send the email (same as Create Report flow)
      await handleSendEmail(reportToSend);
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";

      // Show error dialog
      setDialogMessage({
        title: "Email Send Failed",
        description: errorMessage,
        confirmLabel: "OK",
      });
      setShowConfirmDialog(true);

      if (errorMessage.includes("token is invalid or expired")) {
        showErrorToast(
          "Your Gmail session has expired. Please re-authenticate with Google.",
        );

        // Redirect to authentication or trigger auth flow
        // You might want to redirect to login or trigger OAuth flow
        window.location.href = "/login";
      } else {
        showErrorToast("Failed to send email: " + errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async (report: WeeklyReport) => {
    try {
      console.log("EmailList - handleSendEmail called with report:", report);

      // Check if we have Gmail access
      const gmailAvailable = await emailService.isGmailAvailable();
      console.log("EmailList - Gmail available:", gmailAvailable);

      if (!gmailAvailable) {
        throw new Error(
          "Gmail access is not available. Please authenticate with Google first.",
        );
      }

      // Show sending toast
      showInfoToast("Your report is being sent... Please wait.");

      // Send email using emailService (same as Create Report flow)
      const status = await emailService.sendEmail(report);

      console.log("EmailList - Email sent successfully");

      const updatedReport = {
        ...report,
        status: (status.status === "sent" ? "sent" : "failed") as
          | "sent"
          | "failed",
        sentByEmail: status.status === "sent",
        sentAt: status.sentAt || new Date().toISOString(),
        deliveryStatus: status,
      };

      // Save the updated report
      await reportService.saveReport(updatedReport);

      // Show success message
      showSuccessToast("Your report has been sent successfully");

      // Close the dialog
      setShowConfirmDialog(false);
      setReportToSend(null);

      // Refresh reports list by triggering a page reload or callback
      if (onEditReport) {
        onEditReport(updatedReport);
      }
    } catch (error) {
      console.error("EmailList - Error sending email:", error);

      // Handle token expiration specifically
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      if (errorMessage.includes("token is invalid or expired")) {
        toast.error(
          "Your Gmail session has expired. Please re-authenticate with Google.",
        );

        // Redirect to authentication or trigger auth flow
        if (typeof window !== "undefined" && window.location) {
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        }
      } else {
        toast.error("Failed to send email: " + errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <Card className="w-full shadow-sm animate-pulse">
        <CardHeader>
          <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (reports.length === 0) {
    return (
      <Card className="w-full shadow-sm animate-fade-in mt-4">
        <CardHeader>
          <CardTitle>Report History</CardTitle>
          <CardDescription>A history of your weekly reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <p className="text-muted-foreground mb-4">
              You haven&apos;t created any reports yet
            </p>
            <Badge variant="outline" className="text-muted-foreground">
              Your reports will appear here
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleRowClick = (report: WeeklyReport) => {
    setSelectedReport(report);
  };

  const handleEditClick = (e: React.MouseEvent, report: WeeklyReport) => {
    e.stopPropagation(); // Prevent row click from triggering
    if (onEditReport) {
      onEditReport(report);
    }
  };

  const hasActiveFilters =
    statusFilter !== "all" || startDate !== undefined || endDate !== undefined;

  return (
    <>
      <Card className="w-full shadow-sm animate-fade-in mt-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Report History</CardTitle>
            <CardDescription>A history of your weekly reports</CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                console.log("Debug - All Reports:", reports);
                console.log("Debug - Filtered Reports:", filteredReports);
                alert(
                  `Reports count: ${reports.length}, Filtered: ${filteredReports.length}`,
                );
              }}
              className="flex items-center"
            >
              Debug
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const dateRanges = reports.map((r) => ({
                  id: r.id,
                  dateRange: r.dateRange,
                  date: r.date,
                }));
                console.log("Debug - Date Ranges:", dateRanges);
                alert(`Date ranges: ${JSON.stringify(dateRanges, null, 2)}`);
              }}
              className="flex items-center ml-2"
            >
              Date Debug
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select
                value={statusFilter}
                onValueChange={(value) =>
                  setStatusFilter(value as StatusFilter)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reports</SelectItem>
                  <SelectItem value="sent">Sent Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium">From Date</label>
                {startDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setStartDate(undefined)}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground",
                      startDate && "border-primary",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Select start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium">To Date</label>
                {endDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setEndDate(undefined)}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground",
                      endDate && "border-primary",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Select end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                    disabled={(date) =>
                      startDate ? isBefore(date, startDate) : false
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {(startDate || endDate) && (
            <div className="flex justify-end mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={clearDateFilters}
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Date Filters
              </Button>
            </div>
          )}

          {filteredReports.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No reports match your filters
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="mt-2"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow
                    key={report.id}
                    className="transition-colors hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(report)}
                  >
                    <TableCell className="font-medium">
                      {formatDateRange(report.dateRange, report.date)}
                      {report.dateRange && report.date && (
                        <div className="text-xs text-muted-foreground">
                          Created:{" "}
                          {format(parseISO(report.date), "MMM d, yyyy")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {report.to?.length > 0 && (
                        <div className="text-sm">
                          <span className="font-medium">To:</span>{" "}
                          {report.to.join(", ")}
                        </div>
                      )}
                      {report.cc?.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">CC:</span>{" "}
                          {report.cc.join(", ")}
                        </div>
                      )}
                      {report.bcc?.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">BCC:</span>{" "}
                          {report.bcc.join(", ")}
                        </div>
                      )}
                      {!report.to?.length &&
                        !report.cc?.length &&
                        !report.bcc?.length && (
                          <span className="text-sm text-muted-foreground">
                            No recipients
                          </span>
                        )}
                    </TableCell>
                    <TableCell>{report.subject}</TableCell>
                    <TableCell>
                      {report.clientName ? (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                          {report.clientName}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={
                          report.status ||
                          (report.sentByEmail ? "sent" : "draft")
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {onEditReport && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleEditClick(e, report)}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReport(report);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                          <span className="sr-only">View</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => confirmSendEmail(report)}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Send Email
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedReport}
        onOpenChange={(open) => !open && setSelectedReport(null)}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Report Details</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm font-medium">Recipients:</span>
                {selectedReport?.to?.length > 0 && (
                  <p className="text-sm">
                    <span className="font-medium">To:</span>{" "}
                    {selectedReport.to.join(", ")}
                  </p>
                )}
                {selectedReport?.cc?.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">CC:</span>{" "}
                    {selectedReport.cc.join(", ")}
                  </p>
                )}
                {selectedReport?.bcc?.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">BCC:</span>{" "}
                    {selectedReport.bcc.join(", ")}
                  </p>
                )}
                {!selectedReport?.to?.length &&
                  !selectedReport?.cc?.length &&
                  !selectedReport?.bcc?.length && (
                    <p className="text-sm text-muted-foreground">
                      No recipients
                    </p>
                  )}
              </div>
              <div>
                <span className="text-sm font-medium">Date:</span>
                <p className="text-sm">
                  {selectedReport?.date
                    ? format(parseISO(selectedReport.date), "MMM d, yyyy")
                    : "No date"}
                </p>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium">Subject:</span>
                <p className="text-sm">{selectedReport?.subject}</p>
              </div>
              {selectedReport?.clientName && (
                <div className="col-span-2">
                  <span className="text-sm font-medium">Client:</span>
                  <p className="text-sm">
                    <span className="inline-flex px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                      {selectedReport.clientName}
                    </span>
                  </p>
                </div>
              )}
            </div>
            {selectedReport && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Last Week Tasks</h3>
                  <ul className="list-disc pl-5 mt-2">
                    {selectedReport.last_week_tasks?.map((item, index) => (
                      <li key={index}>
                        {item.value}
                        {item.dependency && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {" "}
                            (Dep: {item.dependency})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Unplanned</h3>
                  <ul className="list-disc pl-5 mt-2">
                    {selectedReport.Unplanned?.map((item, index) => (
                      <li key={index}>
                        {item.value}
                        {item.dependency && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {" "}
                            (Dep: {item.dependency})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Blockers</h3>
                  <ul className="list-disc pl-5 mt-2">
                    {selectedReport.blockers?.map((item, index) => (
                      <li key={index}>
                        {item.value}
                        {item.dependency && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {" "}
                            (Dep: {item.dependency})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Next Week Tasks</h3>
                  <ul className="list-disc pl-5 mt-2">
                    {selectedReport.next_week_tasks?.map((item, index) => (
                      <li key={index}>
                        {item.value}
                        {item.dependency && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {" "}
                            (Dep: {item.dependency})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                {selectedReport.further_beyond &&
                  selectedReport.further_beyond.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Further Beyond:</p>
                      <ul className="list-disc pl-5 mt-1">
                        {selectedReport.further_beyond.map((item, index) => (
                          <li key={index}>
                            {item.value}
                            {item.dependency && (
                              <span className="text-xs text-muted-foreground ml-2">
                                {" "}
                                (Dep: {item.dependency})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                {selectedReport.notes && (
                  <div>
                    <h3 className="text-lg font-semibold">Notes</h3>
                    <p className="mt-2">{selectedReport.notes}</p>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedReport(null)}
                  >
                    Close
                  </Button>
                  {onEditReport && selectedReport && (
                    <Button
                      variant="default"
                      onClick={() => {
                        onEditReport(selectedReport);
                        setSelectedReport(null);
                      }}
                    >
                      Edit Report
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      if (selectedReport) {
                        confirmSendEmail(selectedReport);
                      }
                    }}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Send Email
                  </Button>
                </DialogFooter>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogMessage.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogMessage.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={verifyAndSendEmail}>
              {dialogMessage.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EmailList;
